import { AbstractComponent } from "appworks/components/abstract-component";
import { Components } from "appworks/components/components";
import { CanvasService } from "appworks/graphics/canvas/canvas-service";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { ButtonEvent } from "appworks/graphics/elements/button-element";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { SpineContainer } from "appworks/graphics/pixi/spine-container";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Services } from "appworks/services/services";
import { Contract } from "appworks/utils/contracts/contract";
import { Sequence } from "appworks/utils/contracts/sequence";
import { TweenContract } from "appworks/utils/contracts/tween-contract";
import { Point } from "appworks/utils/geom/point";
import { KeyInput } from "appworks/utils/key-input";
import { KeyboardCode } from "appworks/utils/keyboard";
import { logger } from "appworks/utils/logger";
import { AngleBetween } from "appworks/utils/math/angle";
import { RandomRangeFloat, RandomRangeInt } from "appworks/utils/math/random";
import { Timer } from "appworks/utils/timer";
import { Easing, Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { Graphics } from "pixi.js";
import { Signal, SignalBinding } from "signals";
import { FowlFlingBonusBackgroundComponent } from "./fowl-fling-bonus-background-component";
import { FowlAbstractObstacleSubcomponent } from "./obstacles/fowl-abstract-obstacle-subcomponent";
import { FowlDuckSubcomponent } from "./obstacles/fowl-duck-obstacle-subcomponent";
import { FowlPigSubcomponent } from "./obstacles/fowl-pig-obstacle-subcomponent";
import Matter = require("matter-js");
import { SoundService } from "appworks/services/sound/sound-service";
import { Container } from "appworks/graphics/pixi/container";

export enum FoxAnimStates {
    HIDDEN = "hidden",
    LAUNCH = "fox_launch",
    FLYING = "fly_flat",
    FLYING_SPACE = "fly_space",
    TUMBLING = "", // 2 tumbling speeds, handled elsewhere
    STOPPING = "collision",
    STOPPED = "flat_stars",
}

export enum FlingBonusPrizeTier {
    MINI = "mini",
    MINOR = "minor",
    MAJOR = "major",
    MEGA = "mega",
    GRAND = "grand",
    START = "start",
}

export enum FlingBonusPrizeValues { // ew I hate storing this ew
    "mini" = 20,
    "minor" = 40,
    "major" = 100,
    "mega" = 250,
    "grand" = 1000,
}

export class FowlFlingBonusComponent extends AbstractComponent {
    public static readonly MAX_DISTANCE = 75000;
    public static readonly SECTOR_WIDTH = 1500;

    protected static readonly PLAYBACK_SPEED_MOD = 1.25; // i.e. 1.25 to play back 25% faster

    protected static readonly FLOOR_HEIGHT = 250;

    protected static readonly TIMESTEP = 1000 / 60;
    protected static readonly MAX_STEPS = 7500;
    protected static readonly MAX_PATH_GENERATION_ATTEMPTS = 500;

    protected static readonly LAUNCH_POWER = 30;
    protected static readonly LAUNCH_ANGLE_BOUNDS = {
        min: -0.2,
        max: -Math.PI / 2 + 0.2,
    };

    protected static readonly MAX_X_VELOCITY = 100;

    public onComplete: Signal = new Signal();

    protected engine: Matter.Engine;

    protected paytableContainer: Container;

    protected foxSpine: SpineContainer;
    protected foxBody: Matter.Body;
    protected foxShadow: Sprite;
    protected currentTumbleSound: number;

    protected animState: FoxAnimStates;

    protected floorBody: Matter.Body;

    protected path: FramePosition[] = [];
    protected currentPathFrameIndex: number = 0;
    protected pathGraphic: Graphics;

    protected obstacles: FowlAbstractObstacleSubcomponent[];
    protected obstacleEvents: Array<{
        obstacle: FowlAbstractObstacleSubcomponent;
        time: number;
    }>;

    protected ducks: FowlDuckSubcomponent[];

    protected tweens: Tween[];
    protected launchAngleTween: Tween;
    protected launchAngle: number;

    protected layer = gameLayers.FlingBonus;
    protected prizeTier: FlingBonusPrizeTier;

    protected onRotateListener: SignalBinding;

    constructor() {
        super();

        const keyInput = new KeyInput();
        keyInput.onKeyUp.add((key: string, e: KeyboardEvent) => {
            if (key === KeyboardCode.SPACE && e.shiftKey && e.ctrlKey) {
                if (this.pathGraphic) {
                    this.pathGraphic.destroy();
                    this.pathGraphic = null;
                } else {
                    this.drawDebugPath(this.path);
                }
            }
        });
    }

    public startBonus(prizeTier: FlingBonusPrizeTier) {
        this.prizeTier = prizeTier;

        // Setup paytable
        this.setPaytableValues();

        this.paytableContainer = new Container();
        this.layer.add(this.paytableContainer, false, true);

        for (const elementKey in this.layer.scene.bonus.contents) {
            if (elementKey.includes("paytable")) {
                const element = this.layer.get(elementKey);
                this.paytableContainer.addChild(element);
            }
        }

        // Create matter engine
        this.engine = Matter.Engine.create();

        // Create floor
        this.floorBody = Matter.Bodies.rectangle(
            FowlFlingBonusComponent.MAX_DISTANCE / 2,
            this.layer.bounds.landscape.height - FowlFlingBonusComponent.FLOOR_HEIGHT / 2,
            FowlFlingBonusComponent.MAX_DISTANCE,
            FowlFlingBonusComponent.FLOOR_HEIGHT,
            { isStatic: true }
        );
        Matter.Composite.add(this.engine.world, this.floorBody);

        // fox shadow
        this.foxShadow = this.layer.getSprite("shadow");
        CenterPivot(this.foxShadow);
        this.foxShadow.landscape.y = this.layer.bounds.landscape.height - FowlFlingBonusComponent.FLOOR_HEIGHT;
        this.foxShadow.portrait.y = this.layer.bounds.portrait.height - FowlFlingBonusComponent.FLOOR_HEIGHT;

        // Setup fox spine
        this.foxSpine = this.layer.getSpine("fox_fling");
        this.setFoxAnimState(FoxAnimStates.HIDDEN).execute();
        this.foxSpine.setSpineAnchor({ x: 0, y: 0 });
        this.updateFoxSpine();

        // Obstacles
        this.obstacles = [];
        for (let x = RandomRangeInt(1500, 3000); x < FowlFlingBonusComponent.MAX_DISTANCE; x += RandomRangeInt(2000, 8000)) {
            this.obstacles.push(new FowlPigSubcomponent(new Point(x, this.layer.bounds.landscape.height - FowlFlingBonusComponent.FLOOR_HEIGHT), this.layer));
        }

        // init camera position
        this.onRotateListener = Services.get(CanvasService).onOrientationChange.add(() => this.updateCameraPosition());
        this.updateCameraPosition();

        // Setup launch button
        this.enableLaunchButton(false);

        // Hide arrow
        const arrow = this.layer.getSprite("launch_arrow");
        arrow.visible = false;
    }

    public cleanup() {
        this.tweens?.forEach(tween => tween.stop());

        this.obstacles.forEach(ob => ob.destroy());
        this.ducks.forEach(duck => duck.destroy());

        this.path = [];
        this.currentPathFrameIndex = 0;

        this.pathGraphic?.destroy();
        this.pathGraphic = null;

        Matter.Engine.clear(this.engine);

        this.onRotateListener.detach();
    }

    public startPreLaunch() {
        const arrow = this.layer.getSprite("launch_arrow");
        arrow.visible = true;
        CenterPivot(arrow, null, new Point(0, 0.5));

        this.launchAngle = FowlFlingBonusComponent.LAUNCH_ANGLE_BOUNDS.min;
        this.launchAngleTween = new Tween(this)
            .to({ launchAngle: FowlFlingBonusComponent.LAUNCH_ANGLE_BOUNDS.max }, 1500)
            .yoyo(true)
            .repeat(Number.MAX_SAFE_INTEGER)
            .easing(Easing.Sinusoidal.InOut)
            .onUpdate(() => (arrow.rotation = this.launchAngle))
            .start();

        // this.setFoxAnimState(FoxAnimStates.LAUNCH);
        this.enableLaunchButton(true);
    }

    protected enableLaunchButton(enable: boolean) {
        const launchButton = this.layer.getButton("launch");
        launchButton.setEnabled(enable);

        if (enable) {
            launchButton.once(ButtonEvent.CLICK.getPIXIEventString(), () => {
                launchButton.destroy();
                this.launch().then(() => this.onComplete.dispatch());
            });
        } else {
            launchButton.removeAllListeners(ButtonEvent.CLICK.getPIXIEventString());
        }
    }

    protected resetFoxBody() {
        if (this.foxBody) {
            Matter.Composite.remove(this.engine.world, this.foxBody);
        }

        const startPos = this.layer.getPosition("fox_start");
        this.foxBody = Matter.Bodies.circle(startPos.landscape.x, startPos.landscape.y, startPos.landscape.height / 2, {
            restitution: 0.7,
            friction: 0.01,
            frictionAir: 0.002,
            mass: 85,
            inverseMass: 1 / 85,
        });
        Matter.Composite.add(this.engine.world, this.foxBody);
    }

    protected launch(): Contract {
        this.launchAngleTween.stop();

        // Generate final path
        this.path = this.generatePath(this.launchAngle, FowlFlingBonusComponent.LAUNCH_POWER);

        // Now we know where we're going to land, place the correct prize tile there
        Components.get(FowlFlingBonusBackgroundComponent).setPrize(this.prizeTier, this.path[this.path.length - 1].x);

        // Create the ducks - unlike pigs their collision is determined during playback, as it has no physics effect (and they move)
        this.ducks = [];
        for (let i = 0; i < 10; i++) {
            const duck = new FowlDuckSubcomponent(
                new Point(RandomRangeInt(this.layer.bounds.landscape.width, FowlFlingBonusComponent.MAX_DISTANCE), RandomRangeInt(-500, 200))
            );
            this.ducks.push(duck);
        }

        return new Sequence([
            () =>
                Contract.wrap(() => {
                    this.layer
                        .getSpine("coop")
                        .playOnce("glove_punch")
                        .execute();
                }),
            () => Contract.getTimeoutContract(100),
            () =>
                Contract.wrap(() => {
                    Services.get(SoundService).customEvent("faf_glovepunch");
                    Services.get(SoundService).customEvent("faf_launch");

                    this.layer.getSpine("faf_chicken_march").play("chickens_solo");
                    this.setFoxAnimState(FoxAnimStates.FLYING).execute();
                    Components.get(FowlFlingBonusBackgroundComponent)
                        .showMarker(true, 500)
                        .execute();
                }),
            () => this.playPathTween(this.path),
            () =>
                Contract.wrap(() => {
                    Services.get(SoundService).customEvent("faf_stop_tumble");
                    this.currentTumbleSound = undefined;
                    this.setFoxAnimState(FoxAnimStates.STOPPING, false).then(() => this.setFoxAnimState(FoxAnimStates.STOPPED).execute());
                    this.foxSpine.rotation = 0;
                }),
            () => Contract.getTimeoutContract(3000),
        ]);
    }

    protected generatePath(angleRads: number, power: number, posLimit?: number): FramePosition[] {
        if (!posLimit) {
            posLimit = FowlFlingBonusComponent.MAX_STEPS;
        }

        let positions: FramePosition[];
        let validPathGenerated = false;
        let attemptsRemaining = FowlFlingBonusComponent.MAX_PATH_GENERATION_ATTEMPTS;

        while (!validPathGenerated && --attemptsRemaining > 0) {
            positions = [];
            this.obstacleEvents = [];
            this.obstacles.forEach(obstacle => (obstacle.triggered = false));

            this.resetFoxBody();
            Matter.Body.applyForce(this.foxBody, this.foxBody.position, {
                x: Math.cos(angleRads) * power,
                y: Math.sin(angleRads) * power,
            });
            Matter.Body.setAngularVelocity(this.foxBody, RandomRangeFloat(0.5, 1));

            let time = 0;
            let stationaryTimer = 0;
            let flying = true;
            while (positions.length < posLimit) {
                const foxPosPoint = Point.from(this.foxBody.position);
                this.obstacles.forEach((obstacle: FowlAbstractObstacleSubcomponent) => {
                    if (obstacle.checkCollision(foxPosPoint)) {
                        obstacle.onCollision(this.foxBody);
                        this.obstacleEvents.push({ obstacle, time });
                    }
                });

                // Manually apply a drag force when rolling along the floor
                // Using body friction for this makes the circle act like a wheel and the fox "drives" along the floor when spinning,
                // which is the opposite of what we want
                const onFloor = Boolean(Matter.Collision.collides(this.foxBody, this.floorBody));
                if (onFloor) {
                    if (this.foxBody.velocity.x > 0.1) {
                        Matter.Body.applyForce(this.foxBody, this.foxBody.position, { x: -0.01, y: 0 });
                    }
                    if (this.foxBody.angularVelocity > this.foxBody.velocity.x * 0.1) {
                        Matter.Body.setAngularVelocity(this.foxBody, this.foxBody.angularVelocity * 0.9);
                    }

                    // If landing with little angular velocity, spin him a bit
                    if (flying) {
                        flying = false;
                        Matter.Body.setAngularVelocity(this.foxBody, Math.max(this.foxBody.angularVelocity, 0.2));
                    }
                }

                if (this.foxBody.velocity.x < 0) {
                    // don't allow negative x velocities, moving backward will feel cheaty (so says Matt)
                    Matter.Body.setVelocity(this.foxBody, { x: 0, y: this.foxBody.velocity.y });
                }
                if (this.foxBody.angularVelocity < 0) {
                    Matter.Body.setAngularVelocity(this.foxBody, 0);
                }

                if (this.foxBody.velocity.x > FowlFlingBonusComponent.MAX_X_VELOCITY) {
                    // Cap x velocity
                    Matter.Body.setVelocity(this.foxBody, { x: FowlFlingBonusComponent.MAX_X_VELOCITY, y: this.foxBody.velocity.y });
                }

                Matter.Engine.update(this.engine, FowlFlingBonusComponent.TIMESTEP);
                time += FowlFlingBonusComponent.TIMESTEP;

                positions.push({
                    x: this.foxBody.position.x,
                    y: this.foxBody.position.y,
                    rotation: this.foxBody.angle,
                    velocity: new Point(this.foxBody.velocity.x, this.foxBody.velocity.y),
                    angularVelocity: this.foxBody.angularVelocity,
                    onFloor: onFloor,
                });

                if (Matter.Vector.magnitude(this.foxBody.velocity) < 0.25) {
                    stationaryTimer += FowlFlingBonusComponent.TIMESTEP;
                    if (stationaryTimer >= 250) {
                        break;
                    }
                } else {
                    stationaryTimer = 0;
                }
            }

            const boundaryMod = this.foxBody.position.x % FowlFlingBonusComponent.SECTOR_WIDTH;
            const boundaryTolerance = this.foxBody.circleRadius;
            if (boundaryMod < boundaryTolerance || boundaryMod > FowlFlingBonusComponent.SECTOR_WIDTH - boundaryTolerance) {
                logger.log(`PATH GENERATOR: TOO CLOSE TO BOUNDARY (${boundaryMod}), TRYING AGAIN`);
            } else if (positions.length >= FowlFlingBonusComponent.MAX_STEPS) {
                logger.log("PATH GENERATOR: MAX STEPS REACHED, TRYING AGAIN");
            } else if (positions[positions.length - 1].x >= FowlFlingBonusComponent.MAX_DISTANCE - this.layer.bounds.landscape.width) {
                logger.log("PATH GENERATOR: MAX DISTANCE REACHED, TRYING AGAIN");
            } else {
                validPathGenerated = true;
            }
        }

        if (!validPathGenerated) {
            throw new Error("MAX_PATH_GENERATION_ATTEMPTS");
        }

        let maxHeight = 0;
        let maxAngularVelocity = 0;
        positions.forEach(framePosition => {
            maxHeight = Math.min(maxHeight, framePosition.y); // min because it's negative
            maxAngularVelocity = Math.max(maxAngularVelocity, framePosition.angularVelocity);
        });

        let logString = "Path Generated!";
        logString += "\nAngle: " + angleRads;
        logString += "\nPower: " + power;
        logString += "\nPositions: " + positions.length;
        logString += "\nTime: " + (positions.length * FowlFlingBonusComponent.TIMESTEP * FowlFlingBonusComponent.PLAYBACK_SPEED_MOD) / 1000 + " seconds";
        logString += "\nDistance: " + this.foxBody.position.x + " (" + (this.foxBody.position.x / FowlFlingBonusComponent.MAX_DISTANCE) * 100 + "%)";
        logString += "\nObstacles Hit: " + this.obstacleEvents.length;
        logString += "\nMax Height: " + maxHeight;
        logString += "\nMax Angular Velocity: " + maxAngularVelocity;
        logger.log(logString);

        return positions;
    }

    protected drawDebugPath(path: FramePosition[]) {
        if (path?.length > 0) {
            if (this.pathGraphic) {
                this.pathGraphic.destroy();
            }

            this.pathGraphic = new Graphics();
            this.layer.add(this.pathGraphic, false, true);

            this.pathGraphic.beginFill(0xffffff, 1);

            path.forEach((pos: FramePosition) => {
                this.pathGraphic.drawCircle(pos.x, pos.y, 3);
            });

            this.pathGraphic.endFill();
        }
    }

    protected playPathTween(path: FramePosition[]): Contract {
        const tweenTime = FowlFlingBonusComponent.TIMESTEP * path.length * (1 / FowlFlingBonusComponent.PLAYBACK_SPEED_MOD);

        const pathIndex = { value: 0 };
        let bounceCounter = 1;
        const indexTween = new Tween(pathIndex).to({ value: path.length - 1 }, tweenTime).onUpdate(() => {
            this.currentPathFrameIndex = Math.round(pathIndex.value);
            const currentPos = this.path[this.currentPathFrameIndex];
            const currentPosPoint = Point.from(currentPos);

            this.updateCameraPosition();
            this.updateFoxSpine();
            Components.get(FowlFlingBonusBackgroundComponent).updatePositionMarker(currentPos.x);

            this.ducks.forEach(duck => {
                if (duck.checkCollision(currentPosPoint)) {
                    duck.onCollision(null);
                }
            });

            if (currentPos.onFloor && !this.path[this.currentPathFrameIndex - 1].onFloor) {
                Services.get(SoundService).customEvent("faf_impact_ground_" + Math.min(Math.floor(bounceCounter), 5));
                bounceCounter += 0.5; // so we play each bounce sound twice
            }
        });

        this.obstacleEvents.forEach(oe => {
            Timer.setTimeout(() => {
                oe.obstacle.animate().execute();
                bounceCounter = 1; // reset bounce counter for sound
            }, oe.time / FowlFlingBonusComponent.PLAYBACK_SPEED_MOD);
        });

        return TweenContract.wrapCancellableTween(indexTween);
    }

    protected updateCameraPosition() {
        const canvas = Services.get(CanvasService);
        const layerBoundsCoords = this.layer.bounds[canvas.orientation].coordsToPoint();
        const camPos: Point = layerBoundsCoords.clone();

        const centerX = canvas.viewport[canvas.orientation].width * 0.333 - camPos.x;
        const centerY = canvas.viewport[canvas.orientation].height * 0.4 - camPos.y;

        const currentPosition = this.path[this.currentPathFrameIndex];
        if (currentPosition) {
            if (currentPosition.x > centerX) {
                camPos.x += centerX - currentPosition.x;
            }
            if (currentPosition.y < centerY) {
                camPos.y += centerY - currentPosition.y;
            }
        }

        // set "camera" pos by offsetting the layer container
        this.layer.container.landscape.set(camPos.x, camPos.y);
        this.layer.container.portrait.set(camPos.x, camPos.y);

        // keep paytable visible on screen
        const paytablePos = layerBoundsCoords.subtract(camPos);
        this.paytableContainer.landscape.set(paytablePos.x, paytablePos.y);
        this.paytableContainer.portrait.set(paytablePos.x, paytablePos.y);

        Components.get(FowlFlingBonusBackgroundComponent).setScroll(camPos);
    }

    protected updateFoxSpine() {
        let currentPosition = this.path[this.currentPathFrameIndex];
        if (!currentPosition) {
            const startPos = this.layer.getPosition("fox_start");
            currentPosition = {
                x: startPos.landscape.x + startPos.landscape.width / 2,
                y: startPos.landscape.y + startPos.landscape.height / 2,
                rotation: 0,
                velocity: new Point(),
                angularVelocity: 0,
                onFloor: false,
            };
        }

        this.foxSpine.landscape.setPosition(currentPosition);
        this.foxSpine.portrait.setPosition(currentPosition);

        if (this.animState === FoxAnimStates.FLYING && currentPosition.y < -6500) {
            this.setFoxAnimState(FoxAnimStates.FLYING_SPACE).execute();
            Services.get(SoundService).customEvent("faf_enter_space");
        }

        if (this.animState === FoxAnimStates.FLYING || this.animState === FoxAnimStates.FLYING_SPACE) {
            this.foxSpine.rotation = AngleBetween(this.path[this.currentPathFrameIndex], this.path[Math.max(0, this.currentPathFrameIndex - 1)]) - Math.PI;

            if (this.path[this.currentPathFrameIndex + 1].onFloor) {
                this.setFoxAnimState(FoxAnimStates.TUMBLING).execute();
            }
        } else {
            this.foxSpine.rotation = currentPosition.rotation;
        }

        if (this.animState === FoxAnimStates.TUMBLING) {
            const angularVelocity = this.path[this.currentPathFrameIndex].angularVelocity;

            if (angularVelocity > 0.16) {
                this.foxSpine.play("tumble_fast");
            } else if (angularVelocity < 0.14) {
                // the gap means it won't flicker between the two
                this.foxSpine.play("tumble_medium");
            }

            let tumbleSpeed = undefined;
            if (angularVelocity > 0.18) {
                tumbleSpeed = 1;
            } else if (angularVelocity > 0.12) {
                tumbleSpeed = 2;
            } else if (angularVelocity > 0.08) {
                tumbleSpeed = 3;
            } else {
                tumbleSpeed = 4;
            }
            if (tumbleSpeed !== this.currentTumbleSound) {
                this.currentTumbleSound = tumbleSpeed;
                Services.get(SoundService).customEvent("faf_tumble_" + tumbleSpeed);
            }
        }

        // Update shadow
        this.foxShadow.landscape.x = this.foxShadow.portrait.x = currentPosition.x;

        const distanceFromFloor = this.layer.bounds.landscape.height - FowlFlingBonusComponent.FLOOR_HEIGHT - this.foxBody?.circleRadius - currentPosition.y;
        const shadowAmt = Math.max(0, 1 - distanceFromFloor / 1000);
        this.foxShadow.landscape.scale.set(shadowAmt);
        this.foxShadow.portrait.scale.set(shadowAmt);
        this.foxShadow.alpha = shadowAmt;
        this.foxShadow.visible = this.foxSpine.isPlaying();
    }

    protected setFoxAnimState(state: FoxAnimStates, loop: boolean = true): Contract {
        this.animState = state;

        if (this.foxSpine.hasAnimation(state)) {
            if (loop) {
                this.foxSpine.play(state);
            } else {
                return this.foxSpine.playOnce(state);
            }
        } else {
            this.foxSpine.stop(true);
        }

        return Contract.empty();
    }

    protected setPaytableValues() {
        this.layer.getText("paytable_mini_value").text = `x${FlingBonusPrizeValues.mini}`;
        this.layer.getText("paytable_minor_value").text = `x${FlingBonusPrizeValues.minor}`;
        this.layer.getText("paytable_major_value").text = `x${FlingBonusPrizeValues.major}`;
        this.layer.getText("paytable_mega_value").text = `x${FlingBonusPrizeValues.mega}`;
        this.layer.getText("paytable_grand_value").text = `x${FlingBonusPrizeValues.grand}`;
    }
}

interface FramePosition {
    x: number;
    y: number;
    rotation: number;
    velocity: Point;
    angularVelocity: number;
    onFloor: boolean;
}
